import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { SensorDetail, SensorManual } from "@arc/contracts";
import type { AuthSession } from "../../auth/keycloak.js";
import { sensorApi } from "../../api/client.js";

/** A typed "you are not a SuperAdmin" — the page shows the no-access state instead of an error. */
export class SensorsForbiddenError extends Error {
  readonly code = "SENSORS_FORBIDDEN" as const;
  constructor() {
    super("GET /sensors returned 403");
  }
}
/**
 * A typed 409 carrying the manuals that block the delete (ADR-035 D3). `status` / `code` / `body` are the shape the
 * shared error surface resolves (`resolveError`), so the dialog gets the right copy AND names the manuals.
 */
export class SensorInUseError extends Error {
  readonly code = "SENSOR_IN_USE" as const;
  readonly status = 409 as const;
  readonly body: { manuals: SensorManual[]; elementSettings: number };
  constructor(manuals: SensorManual[], elementSettings = 0) {
    super("DELETE /sensors/:id returned 409");
    this.body = { manuals, elementSettings };
  }
  get manuals(): SensorManual[] {
    return this.body.manuals;
  }
}

/** `GET /sensors` (ADR-035) — every sensor registered in the realm; SuperAdmin-only, so a 403 is a VERDICT, not a failure. */
export function useSensors(session: AuthSession | null) {
  return useQuery({
    queryKey: ["sensors", session?.realm, session?.sub],
    enabled: session !== null,
    staleTime: 60_000,
    queryFn: async () => {
      const res = await sensorApi.listSensors();
      if (res.status === 403) throw new SensorsForbiddenError();
      if (res.status !== 200) throw new Error(`GET /sensors returned ${String(res.status)}`);
      return res.body.sensors;
    },
    // A 403 is the answer, not a hiccup — retrying it just delays the no-access state.
    retry: (count, error) => !(error instanceof SensorsForbiddenError) && count < 1,
  });
}

/** `GET /sensors/:id` — the detail panel: the sensor + which manuals use it. */
export function useSensor(session: AuthSession | null, id: number | null) {
  return useQuery<SensorDetail, Error>({
    queryKey: ["sensor", session?.realm, session?.sub, id],
    enabled: session !== null && id !== null,
    staleTime: 60_000,
    queryFn: async () => {
      const res = await sensorApi.getSensor({ params: { id: id! } });
      if (res.status !== 200) throw new Error(`GET /sensors/${String(id)} returned ${String(res.status)}`);
      return res.body;
    },
  });
}

/** The `fail()` shape the shared error surface understands: status + code + the raw body. */
interface SensorDeleteError extends Error {
  status: number;
  code?: string;
  body: unknown;
}
function failedSensorDelete(id: number, status: number, body: unknown): SensorDeleteError {
  const err = new Error(`DELETE /sensors/${String(id)} returned ${String(status)}`) as SensorDeleteError;
  err.status = status;
  err.code = (body as { code?: string } | undefined)?.code;
  err.body = body;
  return err;
}

/**
 * `DELETE /sensors/:id` — a SOFT delete. A 409 carries the manuals still using the sensor (live, archived or trashed
 * alike) so the caller can name them; the legacy swallowed a failed delete into `console.log`.
 */
export function useDeleteSensor(session: AuthSession | null) {
  const qc = useQueryClient();
  return useMutation<{ deleted: true }, Error, { id: number }>({
    mutationFn: async ({ id }) => {
      const res = await sensorApi.deleteSensor({ params: { id }, body: {} });
      if (res.status === 409) {
        const body = res.body as { manuals?: SensorManual[]; elementSettings?: number };
        throw new SensorInUseError(body.manuals ?? [], body.elementSettings ?? 0);
      }
      // Every other failure keeps status/code/body so the shared surface resolves the RIGHT copy (a 404 from a sensor
      // another admin already deleted is permanent — a bare Error would have read as "check your connection").
      if (res.status !== 200) throw failedSensorDelete(id, res.status, res.body);
      return res.body;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["sensors", session?.realm, session?.sub] });
    },
  });
}
